import React from 'react';
import { Theme, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';
import AudioRing from 'components/CallScribe/Dialer/AudioRing';
import CallTimer from 'components/CallScribe/Dialer/CallTimer';
import Avatar from 'components/CallScribe/ui/Avatar';

export type CallIdentityTone = 'live' | 'pending' | 'error';

export interface ICallIdentityProps {
  name: string;
  subtitle?: string;
  status: string;
  tone: CallIdentityTone;
  compact?: boolean;
  answeredAtMs?: number;
  inputVolume: number;
  outputVolume: number;
  ringActive: boolean;
}

const RING_BOX = 96;
const FACE = 72;
const RING_GAP = 5;

const useCallIdentityStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  return {
    identity: {
      flex: '0 0 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      minWidth: 0,
      maxWidth: '100%',
      textAlign: 'center',
      color: t.onGround,
      '@media (max-height: 799px)': {
        '&[data-compact]': { gap: '0px' },
        '&[data-compact] $ringBox': { transform: 'scale(0.6667)', margin: '-16px 0' },
        '&[data-compact] $name': { ...CS_FONT.title },
        '&[data-compact] $statusLine': { marginTop: 0 },
      },
    },
    ringBox: {
      position: 'relative',
      isolation: 'isolate',
      width: RING_BOX,
      height: RING_BOX,
      marginBottom: 2,
      display: 'grid',
      placeItems: 'center',
      flex: '0 0 auto',
    },
    canvasRing: {
      position: 'absolute',
      inset: 0,
    },
    cssRing: {
      position: 'absolute',
      inset: (RING_BOX - FACE) / 2 - RING_GAP,
      borderRadius: '50%',
      boxSizing: 'border-box',
      pointerEvents: 'none',
    },
    ringPending: {
      border: `2px solid ${t.stageWarn}`,
      borderTopColor: 'transparent',
      animation: '$turn 1400ms linear infinite',
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        borderStyle: 'dashed',
        borderTopColor: t.stageWarn,
      },
    },
    ringError: {
      border: `2px solid ${t.stageEnd}`,
    },
    '@keyframes turn': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    face: {
      position: 'relative',
      zIndex: 1,
      display: 'grid',
      '& [data-avatar] svg': { color: t.onGround },
    },
    faceError: {
      '& [data-avatar] svg': { color: t.stageEnd },
    },
    name: {
      ...CS_FONT.display,
      fontWeight: CS_WEIGHT.heavy,
      letterSpacing: '-0.01em',
      color: t.onGround,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    subtitle: {
      ...CS_FONT.small,
      color: t.onGroundMuted,
      fontVariantNumeric: 'tabular-nums',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    statusLine: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      marginTop: 4,
      ...CS_FONT.body,
      fontWeight: 600,
      color: t.onGroundMuted,
      minWidth: 0,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flex: '0 0 auto',
    },
    dotLive: { background: t.stageCall },
    dotPending: { background: t.stageWarn },
    dotError: { background: t.stageEnd },
    separator: {
      color: t.onGroundFaint,
    },
    timer: {
      ...CS_FONT.body,
      fontWeight: CS_WEIGHT.strong,
      fontVariantNumeric: 'tabular-nums',
      color: t.onGround,
    },
  };
});

const isBlank = (value: string | undefined): boolean => {
  const v = (value ?? '').trim();
  return v === '' || /^[—–-]+$/.test(v);
};

const CallIdentity: React.FC<ICallIdentityProps> = ({
  name,
  subtitle,
  status,
  tone,
  compact = false,
  answeredAtMs,
  inputVolume,
  outputVolume,
  ringActive,
}) => {
  const classes = useCallIdentityStyles();
  const t = csTokens(useTheme<Theme>());
  const person = !isBlank(name) ? name.trim() : (subtitle ?? '').trim();
  const shown = isBlank(person) ? 'No number' : person;
  const under =
    !isBlank(subtitle) && (subtitle ?? '').trim() !== shown ? (subtitle ?? '').trim() : '';
  const error = tone === 'error';

  const dotClass = (() => {
    if (tone === 'live') return classes.dotLive;
    if (error) return classes.dotError;
    return classes.dotPending;
  })();

  const face = (
    <span className={`${classes.face} ${error ? classes.faceError : ''}`}>
      <Avatar
        name={isBlank(person) ? '' : person}
        tone={null}
        surface="stage"
        size="xl"
        icon={error ? <ErrorOutlineRoundedIcon /> : <PhoneRoundedIcon />}
      />
    </span>
  );

  return (
    <div className={classes.identity} data-compact={compact ? '' : undefined}>
      <div className={classes.ringBox} data-ring={tone}>
        {tone === 'live' ? (
          <span className={classes.canvasRing}>
            <AudioRing
              size={RING_BOX}
              inputVolume={inputVolume}
              outputVolume={outputVolume}
              active={ringActive}
              trackColor={t.stageCall}
              techColor={t.stageCall}
              customerColor={t.onGround}
              insets={[2, 7]}
            />
          </span>
        ) : (
          <span
            className={`${classes.cssRing} ${error ? classes.ringError : classes.ringPending}`}
            aria-hidden
          />
        )}
        {face}
      </div>
      <div className={classes.name}>{shown}</div>
      {under ? <div className={classes.subtitle}>{under}</div> : null}
      <div className={classes.statusLine}>
        <span className={`${classes.dot} ${dotClass}`} aria-hidden />
        <span role="status">{status}</span>
        {typeof answeredAtMs === 'number' ? (
          <>
            <span className={classes.separator} aria-hidden>
              ·
            </span>
            <CallTimer running sinceMs={answeredAtMs} className={classes.timer} />
          </>
        ) : null}
      </div>
    </div>
  );
};

export default CallIdentity;
